/* UTILS */
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { computed, type Ref, ref } from 'vue';

import { getSearchedUsers } from '@/api/calls/search/search.api';
/* CALLS */
import { getSharepoolCandidates } from '@/api/calls/sharepool/sharepool.api';
import { getInviteByRecruiters } from '@/api/calls/sharepool_users/sharepool_users.api';
import type { SkillType } from '@/api/calls/skill/skill.types';
import {
  getAffinity,
  getAffinityForCandidatesList,
  getUser,
  getUserManagerFit,
  getUserMotivations,
  getUserPersonality,
  getUserPersonalityTraits,
  getUserProfileSkills,
  getUserRolesFromEmail,
  getUserSkills,
  getUsersManagerFit,
  getUserValues,
  getUserWorkflows
} from '@/api/calls/user/user.api';
/* HELPERS */
import { getCacheDuration, getNextPageValue } from '@/api/helpers/helpers';

export function useGetSharepoolCandidatesQuery(
  uuid,
  {
    searchTerm,
    statusId,
    excludeGroupUuid,
    withPending = false
  }: {
    searchTerm: Ref<string>;
    statusId?: number;
    excludeGroupUuid?: string;
    withPending?: boolean;
  },
  { enabled } = { enabled: ref(true) }
) {
  return useInfiniteQuery({
    queryKey: ['getSharepoolCandidates', { searchTerm, uuid, statusId, excludeGroupUuid }],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => {
      return getSharepoolCandidates(uuid.value, {
        searchTerm: searchTerm.value,
        page: pageParam,
        statusId,
        excludeGroupUuid,
        withPending
      });
    },
    getNextPageParam: getNextPageValue,
    // options
    enabled,
    staleTime: getCacheDuration('medium')
  });
}

export function useGetUserQuery(
  userUuid: Ref<string>,
  fields: string[] = ['aplayer_score'],
  enabled: Ref<boolean> = ref(true)
) {
  return useQuery({
    queryKey: ['getUser', { uuid: userUuid }],
    queryFn: () => getUser(userUuid.value as string, fields),
    enabled,
    staleTime: getCacheDuration('x-long')
  });
}

export function useGetUserWorkflowsQuery(userUuid: Ref<string>, enabled: Ref<boolean> = ref(true)) {
  return useQuery({
    queryKey: ['getUserWorkflows', { uuid: userUuid }],
    queryFn: () => getUserWorkflows(userUuid.value),
    enabled,
    staleTime: getCacheDuration('medium')
  });
}

export function useGetUserAffinityQuery(
  userUuid: Ref<string>,
  comparedContactUuid: Ref<string>,
  enabled: Ref<boolean> = ref(true)
) {
  return useQuery(useGetUserAffinityQueryOptions(userUuid, comparedContactUuid, enabled));
}

export function useGetUserAffinityQueryOptions(
  userUuid: Ref<string>,
  comparedContactUuid: Ref<string>,
  enabled: Ref<boolean> = ref(true)
) {
  return {
    queryKey: ['getUserAffinity', { userUuid, comparedContactUuid }],
    queryFn: () => getAffinity({ uuid: userUuid.value, comparedContactUuid: comparedContactUuid.value }),
    enabled,
    staleTime: getCacheDuration('x-long')
  };
}

export function useGetUserAffinityForCandidatesListQuery(
  userUuid: Ref<string>,
  candidateUuids: Ref<string[]>,
  enabled: Ref<boolean> = ref(true)
) {
  return useQuery(useGetUserAffinityForCandidatesListQueryOptions(userUuid, candidateUuids, enabled));
}

export function useGetUserAffinityForCandidatesListQueryOptions(
  userUuid: Ref<string>,
  candidateUuids: Ref<string[]>,
  enabled: Ref<boolean> = ref(true)
) {
  return {
    queryKey: ['getUserAffinity', { userUuid, candidateUuids }],
    queryFn: () => getAffinityForCandidatesList({ userUuid: userUuid.value, candidateUuids: candidateUuids.value }),
    enabled,
    staleTime: getCacheDuration('x-long')
  };
}

export function useGetUsersManagerFitQuery(
  candidateUuids: Ref<string[] | string>,
  managerUuid: Ref<string>,
  enabled: Ref<boolean> = ref(true)
) {
  return useQuery(useGetUsersManagerFitQueryOptions(candidateUuids, managerUuid, enabled));
}

export function useGetUsersManagerFitQueryOptions(
  candidateUuids: Ref<string[] | string>,
  managerUuid: Ref<string>,
  enabled: Ref<boolean> = ref(true)
) {
  return {
    queryKey: ['getUserAffinity', { managerUuid, candidateUuids }],
    queryFn: () => getUsersManagerFit(managerUuid.value, candidateUuids.value),
    enabled,
    staleTime: getCacheDuration('x-long')
  };
}

export function useGetUserManagerFitQuery(
  candidateUuid: Ref<string>,
  managerUuid: Ref<string>,
  enabled: Ref<boolean> = ref(true)
) {
  return useQuery(useGetUserManagerFitQueryOptions(candidateUuid, managerUuid, enabled));
}

export function useGetUserManagerFitQueryOptions(
  candidateUuid: Ref<string>,
  managerUuid: Ref<string>,
  enabled: Ref<boolean> = ref(true)
) {
  return {
    queryKey: ['getUserAffinity', { managerUuid, candidateUuid }],
    queryFn: () => getUserManagerFit(candidateUuid.value, managerUuid.value),
    enabled,
    staleTime: getCacheDuration('x-long')
  };
}

export function useGetUserSkills(userUuid: string, type: SkillType | undefined = undefined) {
  return useQuery(getUserSkillsOptions(userUuid, type));
}

export function getUserSkillsOptions(userUuid: string, type: SkillType | undefined = undefined) {
  return {
    queryKey: ['userSkillsList', { userUuid, type }],
    queryFn: () => {
      return getUserSkills(userUuid, type);
    },

    staleTime: getCacheDuration('x-long')
  };
}

export function useGetUserProfileSkills(userUuid: Ref<string>) {
  return useQuery({
    queryKey: ['userProfileSkillsList', userUuid],
    queryFn: () => {
      return getUserProfileSkills(userUuid.value);
    },
    enabled: computed(() => !!userUuid.value),
    staleTime: getCacheDuration('x-long')
  });
}

export function useInvitedByRecruitersList({ searchTerm }) {
  return useInfiniteQuery({
    queryKey: ['invitedByRecruitersList', { searchTerm }],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => {
      return getInviteByRecruiters({ search: searchTerm.value, page: pageParam });
    },
    getNextPageParam: getNextPageValue,
    staleTime: getCacheDuration('medium')
  });
}

export function useGetUserPersonality(userUuid: Ref<string>, enabled: Ref<boolean> = ref(true)) {
  return useQuery({
    queryKey: ['userPersonality', { userUuid }],
    queryFn: () => {
      return getUserPersonality(userUuid.value);
    },
    enabled: computed(() => !!userUuid.value && enabled.value),
    staleTime: getCacheDuration('x-long')
  });
}

export function useGetUserPersonalityTraits(userUuid: Ref<string>, enabled: Ref<boolean> = ref(true)) {
  return useQuery({
    queryKey: ['userPersonalityTraits', { userUuid }],
    queryFn: () => {
      return getUserPersonalityTraits(userUuid.value);
    },
    enabled: computed(() => !!userUuid.value && enabled.value),
    staleTime: getCacheDuration('x-long')
  });
}

export function useGetUserValues(userUuid: Ref<string>, enabled: Ref<boolean> = ref(true)) {
  return useQuery({
    queryKey: ['userValues', { userUuid }],
    queryFn: () => {
      return getUserValues(userUuid.value);
    },
    enabled: computed(() => !!userUuid.value && enabled.value),
    staleTime: getCacheDuration('x-long')
  });
}

export function useGetUserMotivations(userUuid: Ref<string>, enabled: Ref<boolean> = ref(true)) {
  return useQuery({
    queryKey: ['userMotivations', { userUuid }],
    queryFn: () => {
      return getUserMotivations(userUuid.value);
    },
    enabled: computed(() => !!userUuid.value && enabled.value),
    staleTime: getCacheDuration('x-long')
  });
}

export function useGetSearchUsers(search: Ref<string>, enabled: Ref<boolean> = ref(true)) {
  return useQuery({
    queryKey: ['getSearchUsers', { search }],
    queryFn: () => {
      return getSearchedUsers(search.value);
    },
    enabled,
    staleTime: getCacheDuration('x-long')
  });
}

export function useGetUserRolesFromEmailOptions(email: Ref<string>) {
  return {
    queryKey: ['getUserRoles', { email }],
    queryFn: () => getUserRolesFromEmail(email.value),
    staleTime: getCacheDuration('x-long')
  };
}
